import { useState } from 'react';

export type CallType = 'solution' | 'knowledge' | 'destruction';
export type CallStep = 'channel' | 'domain' | 'subdomain' | 'type' | 'area' | 'bounty' | 'bountyDetails' | 'cfkConnection' | 'cfkBounty' | 'details' | 'roles' | 'review' | 'complete';

export interface CallFormData {
  channel: string;
  domain: string;
  subdomain: string;
  type: CallType | null;
  functionalArea: string;
  isBounty: boolean;
  bountyAmount: string;
  deadline: string;
  connectedToCfS: boolean;
  cfSHasBounty: boolean;
  title: string;
  description: string;
  requirements: string;
  curator: string;
  contributors: string[];
  confirmer: string;
}

export interface CallCreationMessage {
  id: string;
  role: 'user' | 'assistant';
  content: string;
  step?: CallStep;
  metadata?: any;
}

const INITIAL_FORM_DATA: CallFormData = {
  channel: '',
  domain: '',
  subdomain: '',
  type: null,
  functionalArea: '',
  isBounty: false,
  bountyAmount: '',
  deadline: '',
  connectedToCfS: false,
  cfSHasBounty: false,
  title: '',
  description: '',
  requirements: '',
  curator: '',
  contributors: [],
  confirmer: '',
};

export const useCallCreationFlow = (initialCallType?: CallType | null, hasMultipleChannels: boolean = false) => {
  const [currentStep, setCurrentStep] = useState<CallStep>(
    initialCallType ? (hasMultipleChannels ? 'channel' : 'domain') : 'type'
  );
  const [formData, setFormData] = useState<CallFormData>({
    ...INITIAL_FORM_DATA,
    type: initialCallType || null,
  });
  const [conversationMessages, setConversationMessages] = useState<CallCreationMessage[]>([]);

  const updateFormData = (field: keyof CallFormData, value: any) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addMessage = (message: Omit<CallCreationMessage, 'id'>) => {
    const newMessage: CallCreationMessage = {
      ...message,
      id: `msg-${Date.now()}-${Math.random()}`,
    };
    setConversationMessages(prev => [...prev, newMessage]);
  };

  const progressToNextStep = () => {
    const baseSteps: CallStep[] = ['channel', 'domain', 'subdomain', 'type', 'area', 'bounty'];
    
    // Add conditional steps based on call type and bounty
    if (formData.type === 'knowledge') {
      baseSteps.push('cfkConnection');
      if (formData.connectedToCfS) {
        baseSteps.push('cfkBounty');
      }
    }
    
    if (formData.isBounty) {
      baseSteps.push('bountyDetails');
    }
    
    baseSteps.push('details', 'roles', 'review', 'complete');
    
    const currentIndex = baseSteps.indexOf(currentStep);
    if (currentIndex < baseSteps.length - 1) {
      setCurrentStep(baseSteps[currentIndex + 1]);
    }
  };

  const reset = () => {
    setCurrentStep(initialCallType ? (hasMultipleChannels ? 'channel' : 'domain') : 'type');
    setFormData({ ...INITIAL_FORM_DATA, type: initialCallType || null });
    setConversationMessages([]);
  };

  return {
    currentStep,
    formData,
    conversationMessages,
    updateFormData,
    addMessage,
    progressToNextStep,
    setCurrentStep,
    reset,
  };
};
